import NoSubscriptionIcon from '@mui/icons-material/CreditCardOff';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';

/** Semantic tone for a subscription state; resolved to theme colors by subscriptionChipSx. */
export type SubscriptionTone = 'success' | 'info' | 'warning' | 'error' | 'neutral';

export interface SubscriptionStatusConfig {
  label: string;
  tone: SubscriptionTone;
  icon?: ReactElement;
}

/**
 * App-wide status-chip treatment (soft tinted bg + dark fg, mirroring the
 * `status.*` tokens in theme.ts) — never MUI's filled palette chips.
 */
export const subscriptionChipSx = (tone: SubscriptionTone) => (theme: Theme) => ({
  ...(tone === 'neutral'
    ? { bgcolor: theme.palette.tint.neutralSoft, color: theme.palette.text.secondary }
    : { bgcolor: alpha(theme.palette[tone].main, 0.14), color: theme.palette[tone].dark }),
  '& .MuiChip-icon': { color: 'inherit' },
});

/** Chip config for raw Chargebee subscription statuses, shared across admin pages. */
export const SUBSCRIPTION_STATUS_CONFIG: Record<string, SubscriptionStatusConfig> = {
  active: { label: 'Active', tone: 'success' },
  in_trial: { label: 'In Trial', tone: 'info' },
  non_renewing: { label: 'Non-Renewing', tone: 'warning' },
  paused: { label: 'Paused', tone: 'warning' },
  cancelled: { label: 'Cancelled', tone: 'error' },
  future: { label: 'Future', tone: 'neutral' },
};

/** Chip config for tenants with no subscription at all. Shared so every admin page sends the same signal. */
export const NO_SUBSCRIPTION_CONFIG: SubscriptionStatusConfig = {
  label: 'No Subscription',
  tone: 'error',
  icon: <NoSubscriptionIcon sx={{ fontSize: 14 }} />,
};
